/**
 * AppRoutes — declarative route tree.
 *
 * Two main branches:
 *   /auth/*  — public, wrapped in <AuthLayout>
 *   /app/*   — protected, wrapped in <AppShellLayout>
 *
 * Lazy-loading is intentionally NOT applied yet at the page level.  We're
 * shipping Foundation + Chat first; once the page set grows past 4–5,
 * splitting per-role chunk becomes worthwhile (the system-design plan).
 */
import { Navigate, Route, Routes } from "react-router-dom";

import AuthLayout      from "../layouts/AuthLayout";
import AppShellLayout  from "../layouts/AppShellLayout";
import LoginPage       from "../pages/auth/LoginPage";
import ChatPage        from "../pages/chat/ChatPage";
import DataIndexPage    from "../pages/data/DataIndexPage";
import DataTablePage    from "../pages/data/DataTablePage";
import SavedTablePage   from "../pages/data/SavedTablePage";
import DashboardPage    from "../pages/dashboard/DashboardPage";
import ImportPage       from "../pages/import/ImportPage";
import ProtectedRoute   from "../components/ProtectedRoute";
import AdminPage        from "../pages/admin/AdminPage";

// Placeholder pages for the rest of the nav so the sidebar links don't 404.
// Each is intentionally minimal — they get fleshed out in the next build phase.
function ComingSoon({ title }) {
  return (
    <div className="px-8 lg:px-12 pt-12 pb-16 max-w-[60ch]">
      <div className="reveal">
        <div className="eyebrow">PAGE · IN PROGRESS</div>
      </div>
      <h1 className="reveal display text-[var(--text-3xl)] mt-4" style={{ animationDelay: "120ms" }}>
        {title}
      </h1>
      <p className="reveal text-[var(--text-base)] text-[var(--color-ink-2)] mt-4 leading-[1.6]"
         style={{ animationDelay: "200ms" }}>
        This page is part of the next build phase. The chat surface is the only
        page wired in this iteration so the streaming + auth foundation can be
        verified before the rest of the SPA is layered on.
      </p>
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* ─── Auth branch ───────────────────────────────────────────────── */}
      <Route element={<AuthLayout />}>
        <Route path="/auth/login" element={<LoginPage />} />
      </Route>

      {/* ─── App branch (protected) ───────────────────────────────────── */}
      <Route
        element={
          <ProtectedRoute>
            <AppShellLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/app/chat"          element={<ChatPage />} />
        <Route path="/app/data"            element={<DataIndexPage />} />
        <Route path="/app/data/saved/:id" element={<SavedTablePage />} />
        <Route path="/app/data/:table"    element={<DataTablePage />} />
        <Route path="/app/dashboard"     element={<DashboardPage />} />
        <Route path="/app/import"        element={<ImportPage />} />
        <Route path="/app/admin/users" element={<AdminPage />} />
        <Route path="/app/admin/audit" element={<ComingSoon title="Audit log" />} />
      </Route>

      {/* ─── Defaults / fallbacks ─────────────────────────────────────── */}
      <Route path="/" element={<Navigate to="/app/chat" replace />} />
      <Route path="*" element={<Navigate to="/app/chat" replace />} />
    </Routes>
  );
}
